// Figures that disagree between the organisation's policies.
//
// Each generated document commits the organisation to figures in its Parameter
// table. When two documents set the same parameter differently (a 12-character
// password minimum in one, 14 in another) an auditor reads it as the
// organisation not knowing its own rule. The comparison is
// backend/src/lib/policyContradictions.ts; this panel only shows it, and is
// hidden when there is nothing to show.

import { theme, card } from "../theme";

export interface PolicyContradictionEntry {
  templateId: string;
  title: string;
  versionNumber: number;
  value: string;
  basis: string;
}

export interface PolicyContradiction {
  parameter: string;
  entries: PolicyContradictionEntry[];
}

export function PolicyContradictionsPanel({ contradictions }: { contradictions: PolicyContradiction[] | null }) {
  if (!contradictions || contradictions.length === 0) return null;

  return (
    <div style={{ ...card, marginBottom: 16, padding: "12px 14px", borderColor: theme.color.warning }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>Figures that disagree between your policies</span>
        <span
          style={{
            fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 4, whiteSpace: "nowrap",
            color: theme.color.warningText, background: theme.color.warning + "22", border: `1px solid ${theme.color.warning}`,
          }}
        >
          {contradictions.length} {contradictions.length === 1 ? "figure" : "figures"}
        </span>
      </div>

      <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 6, lineHeight: 1.6 }}>
        These documents commit the organisation to different values for the same thing. Bring them into line before you
        adopt either, so every policy states the same rule.
      </div>

      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
        {contradictions.map((c) => (
          <div
            key={c.parameter}
            style={{
              background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`,
              borderRadius: 3, padding: "9px 12px", fontSize: 12, color: theme.color.text, lineHeight: 1.6,
            }}
          >
            <div style={{ fontWeight: 600, color: theme.color.heading }}>{c.parameter}</div>
            {c.entries.map((e) => (
              <div key={e.templateId} style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
                <span style={{ color: theme.color.muted }}>{e.title} (v{e.versionNumber}):</span>
                <strong>{e.value}</strong>
                {e.basis && <span style={{ fontSize: 11, color: theme.color.faint }}>{e.basis}</span>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
